import type { ExperimentalUI } from 'shared/src'
import type { Ref } from 'vue'
import { computed, ref } from 'vue'

export interface MainSkills {
  qualify_call: boolean
  answer_with_rag: boolean
  step_validation: boolean
  custom_skill: boolean
}

export const DEFAULT_MAIN_SKILLS: Readonly<MainSkills> = Object.freeze({
  qualify_call: true,
  answer_with_rag: false,
  step_validation: false,
  custom_skill: false,
}) satisfies Pick<
  ExperimentalUI['tools'],
  'qualify_call' | 'answer_with_rag' | 'step_validation' | 'custom_skill'
>

type NonStepKey = 'qualify_call' | 'answer_with_rag'

export const normalizeMainSkills = (skills?: Partial<MainSkills>): MainSkills => {
  const normalized: MainSkills = {
    qualify_call: skills?.qualify_call ?? DEFAULT_MAIN_SKILLS.qualify_call,
    answer_with_rag: skills?.answer_with_rag ?? DEFAULT_MAIN_SKILLS.answer_with_rag,
    step_validation: skills?.step_validation ?? DEFAULT_MAIN_SKILLS.step_validation,
    custom_skill: skills?.custom_skill ?? DEFAULT_MAIN_SKILLS.custom_skill,
  }

  // custom_skill and step_validation alone each count as a valid active state
  if (normalized.custom_skill || normalized.step_validation) return normalized

  if (!normalized.qualify_call && !normalized.answer_with_rag) {
    return { ...DEFAULT_MAIN_SKILLS }
  }

  return normalized
}

const updateNonStepSkill = (skills: MainSkills, key: NonStepKey, enabled: boolean): MainSkills => {
  const updated = { ...skills, [key]: enabled }

  // At-least-one-skill invariant (applies only when step_validation is off)
  if (!updated.qualify_call && !updated.answer_with_rag) {
    return skills
  }

  return updated
}

export const useVirtualAgentMainSkills = (skills: Ref<MainSkills>) => {
  // Snapshot of qualify_call/answer_with_rag taken when step_validation is enabled.
  // Restored when step_validation is disabled so the user's prior choice is not lost.
  const previousNonStepSkills = ref<{ qualify_call: boolean; answer_with_rag: boolean } | null>(
    null,
  )

  const qualifyCallsByIntent = computed({
    get: () => skills.value.qualify_call,
    set: (enabled: boolean) => {
      if (enabled && (skills.value.step_validation || skills.value.custom_skill)) {
        // User explicitly chose this skill — discard saved snapshots, turn exclusive skills off
        previousNonStepSkills.value = null
        skills.value = {
          qualify_call: true,
          answer_with_rag: false,
          step_validation: false,
          custom_skill: false,
        }
      } else {
        skills.value = updateNonStepSkill(skills.value, 'qualify_call', enabled)
      }
    },
  })

  const answerQuestionsWithDocumentation = computed({
    get: () => skills.value.answer_with_rag,
    set: (enabled: boolean) => {
      if (enabled && (skills.value.step_validation || skills.value.custom_skill)) {
        previousNonStepSkills.value = null
        skills.value = {
          qualify_call: false,
          answer_with_rag: true,
          step_validation: false,
          custom_skill: false,
        }
      } else {
        skills.value = updateNonStepSkill(skills.value, 'answer_with_rag', enabled)
      }
    },
  })

  const stepValidation = computed({
    get: () => skills.value.step_validation,
    set: (enabled: boolean) => {
      if (enabled) {
        previousNonStepSkills.value = {
          qualify_call: skills.value.qualify_call,
          answer_with_rag: skills.value.answer_with_rag,
        }
        skills.value = {
          qualify_call: false,
          answer_with_rag: false,
          step_validation: true,
          custom_skill: false,
        }
      } else {
        const restored = previousNonStepSkills.value ?? {
          qualify_call: DEFAULT_MAIN_SKILLS.qualify_call,
          answer_with_rag: DEFAULT_MAIN_SKILLS.answer_with_rag,
        }
        previousNonStepSkills.value = null
        skills.value = { ...restored, step_validation: false, custom_skill: false }
      }
    },
  })

  const hasMultipleMainSkills = computed(
    () => qualifyCallsByIntent.value && answerQuestionsWithDocumentation.value,
  )

  return {
    qualifyCallsByIntent,
    answerQuestionsWithDocumentation,
    stepValidation,
    hasMultipleMainSkills,
  }
}
